import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Eye, Mail, UserPlus, ExternalLink } from "lucide-react";
import FunderShell from "./FunderShell";
import { SectionCard, StatusBadge, StageLabel, toneColor } from "./FunderUI";
import { getReviewerPipeline, getReviewerRows } from "../../service/FunderPortalService";

export default function FunderReviewerManagement() {
    return (
        <FunderShell activePage="Reviewer Management">
            {(callId) => <Inner callId={callId} />}
        </FunderShell>
    );
}

// match is a distance-like string ("0.14"); lower = stronger overlap.
function overlapFor(match) {
    const v = parseFloat(match);
    if (v <= 0.20) return { label: "Strong overlap", color: "var(--success)", width: 100 };
    if (v <= 0.35) return { label: "Good overlap", color: "var(--series-blue)", width: 70 };
    return { label: "Moderate overlap", color: "#9aa6a2", width: 40 };
}

const coiColor = (kind) => ({ clear: "var(--success)", warn: "#d4a017", conflict: "var(--danger-muted)" }[kind] || "#9aa6a2");

function InvitedCell({ invited, reminded }) {
    if (!invited || invited.sent === 0) return <span style={{ color: "var(--ink-350)", fontSize: 12.5 }}>No invitations sent</span>;
    return (
        <div style={{ fontSize: 12.5, color: "var(--ink-600)", lineHeight: 1.6 }}>
            <div>{invited.sent} sent</div>
            {invited.accepted > 0 && <div style={{ color: "var(--success)" }}>{invited.accepted} accepted</div>}
            {invited.pending > 0 && (
                <div style={{ color: reminded ? "var(--info)" : "var(--warning)" }}>
                    {invited.pending} pending{reminded ? " · reminded" : invited.pendingNote ? ` (${invited.pendingNote})` : ""}
                </div>
            )}
            {invited.declined > 0 && (
                <div style={{ color: "var(--danger-muted)" }}>{invited.declined} declined{invited.declinedNote ? ` (${invited.declinedNote})` : ""}</div>
            )}
        </div>
    );
}

function ReviewsCell({ reviews }) {
    if (!reviews) return <span style={{ color: "var(--ink-350)" }}>—</span>;
    const pct = reviews.needed ? Math.min(100, Math.round((reviews.in / reviews.needed) * 100)) : 0;
    const color = reviews.in >= reviews.needed ? "var(--success)" : reviews.in > 0 ? "#d4a017" : "#cfcabf";
    return (
        <div style={{ fontSize: 12.5, color: "var(--ink-600)" }}>
            <div style={{ fontWeight: 600 }}>{reviews.in} of {reviews.needed}</div>
            {reviews.note && <div style={{ color: "var(--ink-350)", fontSize: 11.5, fontStyle: "italic" }}>{reviews.note}</div>}
            <div style={{ height: 6, background: "var(--surface-4)", borderRadius: 6, marginTop: 5, overflow: "hidden", width: 90 }}>
                <div style={{ height: 6, width: `${pct}%`, background: color, borderRadius: 6 }} />
            </div>
        </div>
    );
}

function Inner({ callId }) {
    const navigate = useNavigate();
    const pipeline = getReviewerPipeline(callId);
    const rows = getReviewerRows(callId);

    const [expanded, setExpanded] = useState(null);
    const [reminded, setReminded] = useState(() => new Set());
    const [invitedLocal, setInvitedLocal] = useState(() => new Set()); // `${proposalId}:${name}`
    const [pulseId, setPulseId] = useState(null);
    const [glowId, setGlowId] = useState(null);
    const [toast, setToast] = useState(null);

    const shortlisted = pipeline.find((s) => s.label === "Shortlisted")?.value ?? rows.length;
    const candidates = pipeline.find((s) => s.label === "Candidates matched")?.value ?? 0;
    const covered = rows.filter((r) => r.reviews && r.reviews.in >= r.reviews.needed).length;
    const needAttention = rows.filter((r) => r.status === "needs" || r.status === "partial").length;

    const toggle = (id) => setExpanded((cur) => (cur === id ? null : id));

    const handleRemind = (row) => {
        setReminded((s) => new Set(s).add(row.proposalId));
        setToast(`Reminder sent to ${row.applicant}`);
        setTimeout(() => setToast(null), 2600);
    };

    const handleFind = (row) => {
        setExpanded(row.proposalId);
        setPulseId(row.proposalId);
        setTimeout(() => setPulseId(null), 1100);
        setTimeout(() => {
            setGlowId(row.proposalId);
            setTimeout(() => setGlowId(null), 2000);
        }, 500);
    };

    const handleInvite = (row, name) => {
        setInvitedLocal((s) => new Set(s).add(`${row.proposalId}:${name}`));
    };

    return (
        <div className="funder-section-stack">
            {/* Header */}
            <div>
                <div className="funder-kicker">DeepThought-Powered Review Pipeline</div>
                <StageLabel>Stage 4 · Expert review assignment</StageLabel>
                <h1 className="upload-page-heading" style={{ margin: "6px 0 4px" }}>Reviewer Management</h1>
                <p className="upload-page-meta" style={{ maxWidth: 680 }}>
                    Find, invite, and track expert reviewers for shortlisted proposals — powered by DeepThought's knowledge graph of 250,000+ publications.
                </p>
            </div>

            {/* Pipeline funnel */}
            <SectionCard kicker="Pipeline" title="From shortlist to coverage">
                <div className="funder-funnel">
                    {pipeline.map((s, i) => (
                        <React.Fragment key={s.label}>
                            <div className="funder-funnel-stage">
                                <div className="funder-funnel-value" style={{ color: toneColor(s.tone) }}>{s.value}</div>
                                <div className="funder-funnel-label">{s.label}</div>
                            </div>
                            {i < pipeline.length - 1 && (
                                <div className="funder-funnel-sep"><ChevronRight size={18} /></div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
                <p style={{ margin: "12px 0 0", fontSize: 12.5, color: "var(--ink-450)" }}>
                    <strong style={{ color: "var(--success)" }}>{covered} of {shortlisted}</strong> proposals fully covered (≥2 reviews) ·{" "}
                    <strong style={{ color: "var(--warning)" }}>{needAttention} need attention</strong> · DeepThought identified{" "}
                    <strong>{candidates} candidate reviewers</strong> with zero manual searching.
                </p>
            </SectionCard>

            {/* Per-proposal table */}
            <SectionCard kicker="Shortlisted proposals" title="Reviewer assignment status" style={{ overflow: "visible" }}>
                <div style={{ overflowX: "auto" }}>
                    <table className="funder-table">
                        <thead>
                            <tr>
                                <th>Grant #</th>
                                <th>Proposal</th>
                                <th>DT Matches</th>
                                <th>Invited</th>
                                <th>Reviews In</th>
                                <th>Status</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const isOpen = expanded === row.proposalId;
                                const needsFind = (row.invited?.declined > 0) || (row.reviews && row.reviews.in < row.reviews.needed);
                                return (
                                    <React.Fragment key={row.proposalId}>
                                        <tr className={isOpen ? "funder-row-expanded" : ""}>
                                            <td className="funder-grant-no">{row.grantNumber}</td>
                                            <td>
                                                <div className="funder-title-cell" style={{ fontWeight: 600, color: "var(--ink-900)" }}>{row.title}</div>
                                                <div style={{ color: "var(--ink-400)", fontSize: 12 }}>{row.applicant}</div>
                                            </td>
                                            <td>
                                                <button className="funder-link" style={{ background: "none", border: "none", padding: 0, fontSize: 13 }} onClick={() => toggle(row.proposalId)}>
                                                    {row.matches} matches
                                                </button>
                                            </td>
                                            <td><InvitedCell invited={row.invited} reminded={reminded.has(row.proposalId)} /></td>
                                            <td><ReviewsCell reviews={row.reviews} /></td>
                                            <td><StatusBadge status={row.status} /></td>
                                            <td className="funder-rev-actions-cell" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                                <div className="funder-rev-actions" style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                                                    {row.reviews?.in > 0 && (
                                                        <button className="funder-btn-ghost" onClick={() => toggle(row.proposalId)} title="Read reviews">
                                                            <Eye size={13} /> Read
                                                        </button>
                                                    )}
                                                    {row.invited?.pending > 0 && !reminded.has(row.proposalId) && (
                                                        <button className="funder-btn-ghost" onClick={() => handleRemind(row)} title="Send reminder">
                                                            <Mail size={13} /> Remind
                                                        </button>
                                                    )}
                                                    {needsFind && (
                                                        <button className="funder-btn-ghost" style={{ borderColor: "var(--danger-border)", color: "var(--danger-muted)" }} onClick={() => handleFind(row)} title="Find reviewers">
                                                            <UserPlus size={13} /> Find
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr>
                                                <td colSpan={7} style={{ background: "var(--surface-2)", padding: "16px 18px" }}>
                                                    <div className="funder-reviewer-expand">
                                                    <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink-900)", marginBottom: 12 }}>
                                                        DeepThought candidates for {row.title}
                                                    </div>
                                                    <div className="funder-reviewer-grid">
                                                        {row.candidates.map((c) => {
                                                            const ov = overlapFor(c.match);
                                                            const invited = invitedLocal.has(`${row.proposalId}:${c.name}`);
                                                            const showGlow = glowId === row.proposalId && c.status === "Not yet invited" && !invited;
                                                            return (
                                                                <div key={c.name} className={`funder-reviewer-card${pulseId === row.proposalId ? " funder-reviewer-card-pulse" : ""}`}>
                                                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                                                                        <div>
                                                                            <div style={{ fontWeight: 600, fontSize: 13, color: "var(--ink-900)" }}>{c.name}</div>
                                                                            <div style={{ fontSize: 12, color: "var(--ink-400)" }}>{c.institution}</div>
                                                                        </div>
                                                                        <button
                                                                            className={`funder-invite-btn${showGlow ? " funder-invite-glow" : ""}`}
                                                                            onClick={() => handleInvite(row, c.name)}
                                                                            disabled={invited}
                                                                            style={invited ? { opacity: 0.6, cursor: "default" } : undefined}
                                                                        >
                                                                            {invited ? "Invited" : "Invite"}
                                                                        </button>
                                                                    </div>
                                                                    <div className="funder-overlap-bar"><div className="funder-overlap-fill" style={{ width: `${ov.width}%`, background: ov.color }} /></div>
                                                                    <div style={{ fontSize: 11, color: ov.color, fontWeight: 600, marginBottom: 6 }}>{ov.label}</div>
                                                                    <div style={{ fontSize: 12, color: "var(--ink-600)", marginBottom: 8 }}>
                                                                        <span style={{ color: "var(--ink-350)" }}>Research overlap: </span>{c.connected}
                                                                    </div>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                                                                        <span className="funder-coi-dot" style={{ background: coiColor(c.coi.kind) }} />
                                                                        <span style={{ color: coiColor(c.coi.kind) }}>{c.coi.label}</span>
                                                                    </div>
                                                                    {c.status && <div style={{ fontSize: 11, color: "var(--ink-350)", marginTop: 6 }}>Status: {invited ? "Invited" : c.status}</div>}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>

                                                    {row.received && row.received.length > 0 && (
                                                        <>
                                                            <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink-900)", margin: "18px 0 12px" }}>Reviews received</div>
                                                            <div className="funder-reviewer-grid">
                                                                {row.received.map((rv, i) => (
                                                                    <div key={i} className="funder-reviewer-card">
                                                                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                                                                            <span style={{ fontWeight: 600, fontSize: 13, color: "var(--ink-900)" }}>{rv.reviewer}</span>
                                                                            <span style={{ color: "var(--info)", fontWeight: 700, fontSize: 13 }}>Score {rv.score}</span>
                                                                        </div>
                                                                        <div style={{ fontSize: 11, color: "var(--ink-350)", margin: "2px 0 6px" }}>Submitted {rv.submitted}</div>
                                                                        <div style={{ fontSize: 12, color: "var(--ink-600)", lineHeight: 1.45 }}>{rv.snippet}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <p style={{ margin: "14px 0 0", fontSize: 11.5, color: "var(--ink-350)", fontStyle: "italic", lineHeight: 1.5 }}>
                    DeepThought matches reviewers on the overlap between their published work and the proposal's research area, then screens for conflicts of interest using co-authorship and institutional data.
                </p>
            </SectionCard>

            <button className="funder-link" style={{ background: "none", border: "none", padding: 0 }} onClick={() => navigate("/funder/committee")}>
                Continue to Committee <ExternalLink size={13} />
            </button>

            {toast && (
                <div className="funder-toast" style={{ position: "fixed", right: 20, bottom: 20, zIndex: 4000, background: "var(--brand)", color: "#fff", padding: "10px 16px", borderRadius: 10, fontSize: 13, boxShadow: "0 6px 20px rgba(0,0,0,0.2)" }}>
                    {toast}
                </div>
            )}
        </div>
    );
}
